import type { SelfDescribingJson } from '@snowplow/browser-tracker';
import { trackSelfDescribingEvent } from '@snowplow/browser-tracker';

import { getUrlParts } from '../../url/url';

export type RedirectType =
  | 'search-ads'
  | 'rotation-tool'
  | 'organic'
  | 'fast-track'
  | 'email'
  | 'under-65-to-over-65';

export type SourceCategory =
  | 'landing'
  | 'survey'
  | 'fas'
  | 'typ'
  | 'quotes'
  | 'email'
  | 'application'
  | 'start-application'
  | 'details'
  | 'eligibility';

export type SourceFunnel =
  | 'medicare-advantage'
  | 'medicare'
  | 'health'
  | 'final-expense';

interface RedirectionEventData {
  sourceCategory: SourceCategory;
  redirectType: RedirectType;
  targetApplication: string;
  targetUrl: string;
  sourceFunnel: SourceFunnel;
  description?: string;
}

/**
 * Track Redirection event in Snowplow.
 * v.0.18.1;
 * @public
 *
 * @param eventData - Object that contains event properties described in the Redirection document
 * (see https://healthcareinc.atlassian.net/l/cp/oE221wPW ).
 *
 * @example
 * ```ts
 *
 * import {trackRedirection} from '@hc-frontend/third-party-snowplow';
 *
 * trackRedirection({
 *   sourceCategory: 'survey',
 *   redirectType: 'under-65-to-over-65',
 *   targetApplication: 'healthcare.com',
 *   targetUrl: 'https://www.healthcare.com/medicare-supplement/survey/personal-information/',
 *   sourceFunnel: 'health',
 * });
 * ```
 */
function trackRedirection(
  eventData: RedirectionEventData,
  contexts?: Array<SelfDescribingJson> | null,
): void {
  const {
    sourceCategory,
    redirectType,
    targetApplication,
    targetUrl,
    sourceFunnel,
    description,
  } = eventData;
  const { hostname, path, query } = getUrlParts(targetUrl);
  trackSelfDescribingEvent({
    event: {
      schema: 'iglu:com.healthcare/redirection/jsonschema/1-1-0',
      data: {
        source_category: sourceCategory,
        redirect_type: redirectType,
        target_application: targetApplication.slice(0, 500),
        target_url_host: hostname.slice(0, 500),
        target_url_path: path.slice(0, 3_000),
        target_url_query: query.slice(0, 3_000),
        source_funnel: sourceFunnel,
        description: description?.slice(0, 3_000),
      },
    },
    context: contexts,
  });
}

export { trackRedirection };
